/* eslint-disable no-underscore-dangle */
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Optional,
  Output,
  Provider,
  SimpleChanges,
  OnChanges,
  ViewChild,
} from '@angular/core';
import {
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  UntypedFormControl as FormControl,
} from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { dirtyOrTouchedErrorStateMatcher } from '../../constants/dirty-or-touched-error-state-matcher';
import { validationMessages } from '../../messages';
import { IdName } from '../../models/id-name';

export const WRAPPER_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line no-use-before-define
  useExisting: forwardRef(() => UiAutocompleteComponent),
  multi: true,
};

export interface UiAutocompleteOption extends IdName {
  optionClass?: string;
  optionSuffix?: string;
  indent?: number;
}

@Component({
  selector: 'app-ui-autocomplete',
  templateUrl: './ui-autocomplete.component.html',
  styleUrls: ['./ui-autocomplete.component.scss'],
  providers: [WRAPPER_CONTROL_VALUE_ACCESSOR],
  standalone: false,
  host: {
    '[class.ui-form-field--no-hint-row]': 'showClearButton',
  },
})
export class UiAutocompleteComponent implements OnDestroy, OnChanges {
  @Input() label: string;
  @Input() description = '';
  @Input() options: UiAutocompleteOption[] | null;
  @Input() initialValue: string | undefined;
  @Input() searchTerm: FormControl;
  @Input() placeholder: string;
  @Input() floatLabel: 'always' | 'auto' = 'auto';
  @Input() showList = true;
  @Input() required = false;
  @Input() renderIcons = false;
  @Input() messages = validationMessages;
  @Input() showClearButton = false;
  @Output() readonly emitSearch = new EventEmitter<boolean>();

  @Output() readonly changeHandler: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  filteredOptions: Observable<UiAutocompleteOption[] | undefined>;
  readonly errorStateMatcher = dirtyOrTouchedErrorStateMatcher;

  private controlSubscription?: Subscription;

  constructor(
    private translate: TranslateService,
    @Optional() private readonly parentForm: FormGroupDirective
  ) {}

  get hasValue(): boolean {
    return Boolean(this.searchTerm?.value);
  }

  get showErrorIcon(): boolean {
    return this.errorStateMatcher.isErrorState(this.searchTerm, this.parentForm);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.searchTerm.setValue(this.searchTerm.value);
    }
    if (changes.searchTerm) {
      this.bindControl();
    }
  }

  ngOnDestroy(): void {
    this.controlSubscription?.unsubscribe();
  }

  search(): void {
    this.emitSearch.emit(true);
  }

  onSelect(event: MatOptionSelectionChange, option: UiAutocompleteOption) {
    // onSelectionChange fires both for the new selected, and the one being unselected, in that order
    if (event.source.selected) {
      this.setValue(option);
    }
  }

  onChange(event: any): void {
    this.changeHandler.emit(event);
  }

  clearInput(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasValue || this.searchTerm.disabled) {
      return;
    }

    this.searchTerm.setValue('');
    this.searchTerm.markAsDirty();
    this.searchTerm.markAsTouched();
    this.input.nativeElement.focus();
  }

  setValue(option: UiAutocompleteOption) {
    this.searchTerm.setValue(option);
  }

  nameof = (option: UiAutocompleteOption): string => {
    return option ? this.translate.instant(option.name || `${option}`) || '' : '';
  };

  private filter(value: string): UiAutocompleteOption[] | undefined {
    const filterValue = value.toLocaleLowerCase();
    return this.options?.filter((option) => option.name.toLocaleLowerCase().includes(filterValue));
  }

  private bindControl(): void {
    this.controlSubscription?.unsubscribe();
    this.filteredOptions = this.searchTerm.valueChanges.pipe(
      startWith<string | UiAutocompleteOption>(''),
      debounceTime(100),
      map((value: string | UiAutocompleteOption) =>
        typeof value === 'string' ? value : value.name
      ),
      map((name: string) => (name ? this.filter(name) : this.options?.slice()))
    );

    if (this.initialValue) {
      const option = this.options?.find((o) => o.id === this.initialValue);
      if (option) {
        this.setValue(option);
      } else {
        throw Error(`no option found for ${this.initialValue}`);
      }
    }

    this.controlSubscription = this.searchTerm.valueChanges.subscribe((value) => {
      this.changeHandler.emit(value);
    });
  }
}
